// =====================================================
// COMPONENTE DATOS CLIMÁTICOS
// src/pages/Agro/Establecimiento/DatosClimaticos.js
//
// La pantalla tiene un solo trabajo: que el analista de riesgo pueda juzgar,
// de un vistazo, si el establecimiento tuvo agua suficiente y si hubo eventos
// que comprometan la campaña.
//
// Por eso el elemento central es el BALANCE HÍDRICO (demanda vs. aporte),
// que es la lectura agronómica real, y no seis recuadros con el mismo peso
// visual donde el dato importante se pierde entre los accesorios.
// =====================================================

import React, { useState, useEffect } from "react"
import { Row, Col, Card, CardBody, Badge, Spinner, Alert } from "reactstrap"
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import dayjs from "dayjs"

import { getClimaEstablecimiento, getHistoricoClima } from "api/agro"
import PanelAlertas from "pages/Agro/Alertas/PanelAlertas"

// Iconos de OpenWeatherMap -> mdi
const ICONO_MDI = {
  "01": "mdi-weather-sunny",
  "02": "mdi-weather-partly-cloudy",
  "03": "mdi-weather-cloudy",
  "04": "mdi-weather-cloudy",
  "09": "mdi-weather-pouring",
  10: "mdi-weather-rainy",
  11: "mdi-weather-lightning",
  13: "mdi-weather-snowy",
  50: "mdi-weather-fog",
}

const iconoDe = codigo => {
  if (!codigo) return "mdi-weather-cloudy"
  return ICONO_MDI[String(codigo).substring(0, 2)] || "mdi-weather-cloudy"
}

const num = (valor, decimales = 0) =>
  valor == null ? null : Number(valor).toFixed(decimales)

const DatosClimaticos = ({ idEstablecimiento }) => {
  const [loading, setLoading] = useState(true)
  const [clima, setClima] = useState(null)
  const [historico, setHistorico] = useState([])

  useEffect(() => {
    if (idEstablecimiento) {
      loadData()
    }
  }, [idEstablecimiento])

  const loadData = async () => {
    setLoading(true)
    try {
      const [climaData, historicoData] = await Promise.all([
        getClimaEstablecimiento(idEstablecimiento),
        getHistoricoClima(
          idEstablecimiento,
          dayjs().subtract(30, "day").format("YYYY-MM-DD"),
          dayjs().format("YYYY-MM-DD"),
        ).catch(() => []),
      ])
      setClima(climaData)
      setHistorico(historicoData || [])
    } catch (error) {
      console.error("Error cargando clima:", error)
      setClima({ error: error.message || "Error al cargar datos climáticos" })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2 text-muted">Cargando datos climáticos...</p>
      </div>
    )
  }

  if (!clima || clima.error) {
    return (
      <Alert color="warning">
        <i className="mdi mdi-alert me-2" />
        {clima?.error || "No se pudieron cargar los datos climáticos"}
      </Alert>
    )
  }

  const climaActual = clima.climaActual || clima
  const pronostico = clima.pronostico
  const indices = clima.indicesAgroclimaticos

  const tieneClimaActual =
    climaActual &&
    (climaActual.temperaturaActual !== undefined ||
      climaActual.estacion !== undefined)

  const chartData = (historico || []).map(h => ({
    fecha: dayjs(h.fecha).format("DD/MM"),
    tempMax: h.temperaturaMaxima,
    tempMin: h.temperaturaMinima,
    precipitacion: h.precipitacionMm || 0,
  }))

  // ---------- Balance hídrico ----------
  const demanda = indices?.evapotranspiracion
    ? Number(indices.evapotranspiracion)
    : null
  const aporte = indices?.precipitacionAcumulada30d
    ? Number(indices.precipitacionAcumulada30d)
    : null
  const deficit = indices?.deficitHidrico
    ? Number(indices.deficitHidrico)
    : null

  const porcentajeCubierto =
    demanda && demanda > 0 && aporte != null
      ? Math.min(100, Math.round((aporte / demanda) * 100))
      : null

  const severidad =
    porcentajeCubierto == null
      ? null
      : porcentajeCubierto >= 70
        ? { color: "#0ab39c", texto: "Demanda cubierta", badge: "success" }
        : porcentajeCubierto >= 35
          ? { color: "#f7b84b", texto: "Déficit moderado", badge: "warning" }
          : { color: "#f06548", texto: "Déficit severo", badge: "danger" }

  const heladas = indices?.heladas

  return (
    <>
      {/* Alertas meteorologicas oficiales (SMN / feeds CAP).
          Antes este bloque leia clima.alertas, que venia de una consulta en
          vivo a ws.smn.gob.ar: el host bloqueado por Cloudflare. El try/catch
          del backend se comia el error, asi que el panel quedaba siempre
          vacio sin que nada lo denunciara. Ahora sale de alerta_climatica. */}
      <Row className="mb-3">
        <Col md={12}>
          <PanelAlertas
            alcance="establecimiento"
            idEstablecimiento={idEstablecimiento}
            ocultarSiVacio
          />
        </Col>
      </Row>

      {/* Serie incompleta */}
      {indices && indices.datosSuficientes === false && (
        <Alert color="warning" className="d-flex align-items-center">
          <i className="mdi mdi-database-alert me-2" style={{ fontSize: 20 }} />
          <span>
            Serie climática incompleta
            {indices.diasConDatos30d != null
              ? ` — ${indices.diasConDatos30d} de 30 días con registro`
              : ""}
            . Los acumulados no se informan hasta contar con datos suficientes.
          </span>
        </Alert>
      )}

      <Row>
        {/* ---------- Condiciones actuales ---------- */}
        <Col lg={4}>
          <Card className="h-100">
            <CardBody>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h6
                    className="text-uppercase text-muted mb-1"
                    style={{ letterSpacing: ".5px", fontSize: 11 }}
                  >
                    Ahora
                  </h6>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    {climaActual.estacion && `${climaActual.estacion}`}
                    {climaActual.provincia || clima.provincia
                      ? `, ${climaActual.provincia || clima.provincia}`
                      : ""}
                  </div>
                </div>
                {climaActual.fechaHora && (
                  <small className="text-muted">
                    {dayjs(climaActual.fechaHora).format("HH:mm")}
                  </small>
                )}
              </div>

              {tieneClimaActual ? (
                <>
                  <div className="d-flex align-items-center mb-3">
                    <i
                      className={`mdi ${iconoDe(climaActual.iconoEstado)} text-primary me-3`}
                      style={{ fontSize: 48, lineHeight: 1 }}
                    />
                    <div>
                      <div
                        style={{ fontSize: 40, fontWeight: 300, lineHeight: 1 }}
                      >
                        {num(climaActual.temperaturaActual)}
                        <span style={{ fontSize: 22 }}>°C</span>
                      </div>
                      <div className="text-muted" style={{ fontSize: 13 }}>
                        {climaActual.estadoTiempo || "—"}
                        {climaActual.temperaturaSensacion != null &&
                          ` · ST ${num(climaActual.temperaturaSensacion)}°`}
                      </div>
                    </div>
                  </div>

                  <div className="border-top pt-3">
                    <DatoLinea
                      icono="mdi-water-percent"
                      etiqueta="Humedad"
                      valor={
                        climaActual.humedadRelativa != null
                          ? `${num(climaActual.humedadRelativa)}%`
                          : "—"
                      }
                    />
                    <DatoLinea
                      icono="mdi-weather-windy"
                      etiqueta="Viento"
                      valor={
                        climaActual.velocidadVientoKmh != null
                          ? `${num(climaActual.velocidadVientoKmh, 1)} km/h ${
                              climaActual.direccionViento || ""
                            }`
                          : "—"
                      }
                    />
                    <DatoLinea
                      icono="mdi-gauge"
                      etiqueta="Presión"
                      valor={
                        climaActual.presionAtmosferica != null
                          ? `${num(climaActual.presionAtmosferica)} hPa`
                          : "—"
                      }
                    />
                  </div>

                  {/* Ventana de aplicación: dato operativo, no decorativo */}
                  {indices &&
                    indices.condicionAplicacionAgroquimicos != null && (
                      <div
                        className={`mt-3 p-2 rounded d-flex align-items-start ${
                          indices.condicionAplicacionAgroquimicos
                            ? "bg-success bg-opacity-10"
                            : "bg-warning bg-opacity-10"
                        }`}
                      >
                        <i
                          className={`mdi ${
                            indices.condicionAplicacionAgroquimicos
                              ? "mdi-spray text-success"
                              : "mdi-spray-bottle text-warning"
                          } me-2`}
                        />
                        <div style={{ fontSize: 12 }}>
                          <strong>
                            {indices.condicionAplicacionAgroquimicos
                              ? "Condiciones aptas para aplicar"
                              : "No apto para aplicar"}
                          </strong>
                          {indices.motivoNoAplicacion && (
                            <div className="text-muted">
                              {indices.motivoNoAplicacion}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </>
              ) : (
                <p className="text-muted py-4 text-center mb-0">
                  Sin datos de clima actual
                </p>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* ---------- Pronóstico ---------- */}
        <Col lg={8}>
          <Card className="h-100">
            <CardBody>
              <h6
                className="text-uppercase text-muted mb-3"
                style={{ letterSpacing: ".5px", fontSize: 11 }}
              >
                Próximos días
              </h6>

              {pronostico && pronostico.dias && pronostico.dias.length > 0 ? (
                <Row className="g-2">
                  {pronostico.dias.map((dia, idx) => {
                    const fecha = dia.fecha
                      ? dayjs(dia.fecha)
                      : dayjs().add(idx, "day")
                    // El día en curso trae solo las horas que faltan: se marca
                    // como parcial en vez de mostrar una máxima que no lo es
                    const parcial =
                      idx === 0 &&
                      dia.temperaturaMaxima != null &&
                      dia.temperaturaMinima != null &&
                      Number(dia.temperaturaMaxima) ===
                        Number(dia.temperaturaMinima)

                    return (
                      <Col key={dia.fecha || idx}>
                        <div className="text-center border rounded py-3 h-100">
                          <div
                            className="text-uppercase fw-semibold"
                            style={{ fontSize: 11, letterSpacing: ".5px" }}
                          >
                            {idx === 0 ? "Hoy" : fecha.format("ddd D")}
                          </div>

                          <i
                            className={`mdi ${iconoDe(
                              dia.iconoManana || dia.iconoTarde,
                            )} text-primary d-block my-2`}
                            style={{ fontSize: 26 }}
                          />

                          <div style={{ fontSize: 15 }}>
                            <span className="fw-semibold">
                              {num(dia.temperaturaMaxima) ?? "—"}°
                            </span>
                            <span className="text-muted ms-1">
                              {num(dia.temperaturaMinima) ?? "—"}°
                            </span>
                          </div>

                          <div
                            className="text-muted mt-1"
                            style={{ fontSize: 11, minHeight: 15 }}
                          >
                            {dia.precipitacionEsperadaMm > 0
                              ? `${num(dia.precipitacionEsperadaMm, 1)} mm`
                              : dia.probabilidadLluvia > 0
                                ? `${num(dia.probabilidadLluvia)}%`
                                : ""}
                          </div>

                          {parcial && (
                            <div
                              className="text-muted fst-italic"
                              style={{ fontSize: 10 }}
                            >
                              parcial
                            </div>
                          )}
                        </div>
                      </Col>
                    )
                  })}
                </Row>
              ) : (
                <p className="text-muted text-center py-4 mb-0">
                  Sin pronóstico disponible
                </p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ---------- Balance hídrico: el dato central ---------- */}
      {indices && (
        <Card className="mt-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6
                className="text-uppercase text-muted mb-0"
                style={{ letterSpacing: ".5px", fontSize: 11 }}
              >
                Balance hídrico · últimos 30 días
              </h6>
              {severidad && (
                <Badge color={severidad.badge} pill>
                  {severidad.texto}
                </Badge>
              )}
            </div>

            {demanda != null && aporte != null ? (
              <Row className="align-items-center">
                <Col md={8}>
                  {/* Barra: cuánto de la demanda cubrió la lluvia */}
                  <div
                    className="position-relative rounded"
                    style={{
                      height: 34,
                      background: "#eff2f7",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${porcentajeCubierto}%`,
                        height: "100%",
                        background: severidad.color,
                        transition: "width .4s ease",
                      }}
                    />
                    <div
                      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center px-3"
                      style={{ fontSize: 13 }}
                    >
                      <span className="fw-semibold text-white">
                        {porcentajeCubierto}% cubierto
                      </span>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between mt-2">
                    <small className="text-muted">
                      Aporte por lluvia{" "}
                      <strong className="text-dark">{num(aporte)} mm</strong>
                    </small>
                    <small className="text-muted">
                      Demanda (ET₀){" "}
                      <strong className="text-dark">
                        {num(demanda, 1)} mm
                      </strong>
                    </small>
                  </div>

                  {indices.fuenteEvapotranspiracion && (
                    <small
                      className="text-muted d-block mt-2"
                      style={{ fontSize: 11 }}
                    >
                      ET₀ estimada por {indices.fuenteEvapotranspiracion}
                    </small>
                  )}
                </Col>

                <Col md={4} className="text-md-end mt-3 mt-md-0">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Déficit a reponer con riego
                  </div>
                  <div
                    style={{ fontSize: 34, fontWeight: 300, lineHeight: 1.1 }}
                    className={deficit > 0 ? "text-danger" : "text-success"}
                  >
                    {num(deficit, 0)} <span style={{ fontSize: 18 }}>mm</span>
                  </div>
                </Col>
              </Row>
            ) : (
              <p className="text-muted mb-0">
                Sin datos suficientes para calcular el balance hídrico.
              </p>
            )}

            {/* Indicadores secundarios */}
            <Row className="mt-4 g-3">
              <Col sm={6} md={3}>
                <Metrica
                  valor={
                    indices.precipitacionAcumulada90d != null
                      ? `${num(indices.precipitacionAcumulada90d)} mm`
                      : "—"
                  }
                  etiqueta="Lluvia 90 días"
                />
              </Col>
              <Col sm={6} md={3}>
                <Metrica
                  valor={heladas != null ? `${heladas}` : "—"}
                  etiqueta="Días con helada (90 d)"
                  destacado={heladas > 0}
                />
              </Col>
              <Col sm={6} md={3}>
                <Metrica
                  valor={
                    indices.diasOptimosLaboreo != null
                      ? `${indices.diasOptimosLaboreo}`
                      : "—"
                  }
                  etiqueta="Días aptos de laboreo (30 d)"
                />
              </Col>
              <Col sm={6} md={3}>
                <Metrica
                  valor={
                    indices.gradosDiaAcumulados != null
                      ? num(indices.gradosDiaAcumulados)
                      : "—"
                  }
                  etiqueta="Grados día (base 10)"
                />
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      {/* ---------- Serie histórica ---------- */}
      {chartData.length > 0 && (
        <Card className="mt-3">
          <CardBody>
            <h6
              className="text-uppercase text-muted mb-3"
              style={{ letterSpacing: ".5px", fontSize: 11 }}
            >
              Temperatura y lluvia · últimos 30 días
            </h6>

            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart
                data={chartData}
                margin={{ top: 5, right: 10, left: -10, bottom: 0 }}
              >
                <CartesianGrid stroke="#eff2f7" vertical={false} />
                <XAxis
                  dataKey="fecha"
                  tick={{ fontSize: 11, fill: "#878a99" }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  yAxisId="temp"
                  tick={{ fontSize: 11, fill: "#878a99" }}
                  tickLine={false}
                  axisLine={false}
                  unit="°"
                />
                <YAxis
                  yAxisId="precip"
                  orientation="right"
                  tick={{ fontSize: 11, fill: "#878a99" }}
                  tickLine={false}
                  axisLine={false}
                  unit=" mm"
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 6,
                    border: "1px solid #e9ebec",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="plainline" />

                {/* La lluvia va en barras: son eventos diarios discretos.
                    Como línea sugiere una continuidad que no existe. */}
                <Bar
                  yAxisId="precip"
                  dataKey="precipitacion"
                  fill="#4b93d1"
                  name="Lluvia (mm)"
                  barSize={10}
                  radius={[2, 2, 0, 0]}
                />
                <Line
                  yAxisId="temp"
                  type="monotone"
                  dataKey="tempMax"
                  stroke="#f06548"
                  name="Máxima"
                  dot={false}
                  strokeWidth={2}
                />
                <Line
                  yAxisId="temp"
                  type="monotone"
                  dataKey="tempMin"
                  stroke="#4a9dd8"
                  name="Mínima"
                  dot={false}
                  strokeWidth={2}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      )}
    </>
  )
}

// =====================================================
// Subcomponentes
// =====================================================

const DatoLinea = ({ icono, etiqueta, valor }) => (
  <div className="d-flex justify-content-between align-items-center py-1">
    <span className="text-muted" style={{ fontSize: 13 }}>
      <i className={`mdi ${icono} me-2`} />
      {etiqueta}
    </span>
    <span style={{ fontSize: 13 }}>{valor}</span>
  </div>
)

const Metrica = ({ valor, etiqueta, destacado }) => (
  <div className="border-start ps-3" style={{ borderWidth: 2 }}>
    <div
      style={{ fontSize: 22, fontWeight: 400, lineHeight: 1.2 }}
      className={destacado ? "text-warning" : ""}
    >
      {valor}
    </div>
    <div className="text-muted" style={{ fontSize: 12 }}>
      {etiqueta}
    </div>
  </div>
)

export default DatosClimaticos
